/**
 * Motion and interaction for the deck's pages: the hero's fan and sunrise,
 * the pinned card ritual, the drifting card band, the swipeable deck, text
 * reveals, card tilt, the sticky buy bar, quantity pickers and smoothly
 * opening answers.
 *
 * Each piece is a custom element, so it sets itself up when it lands on the
 * page (theme editor reloads included) and tidies up when it leaves. Visitors
 * who prefer reduced motion get the same content without the movement.
 */
(() => {
  const { gsap, ScrollTrigger, SplitText } = window;
  const hasGsap = Boolean(gsap && ScrollTrigger && SplitText);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const canAnimate = () => hasGsap && !reducedMotion.matches;

  if (hasGsap) {
    gsap.registerPlugin(ScrollTrigger, SplitText);
    ScrollTrigger.config({ ignoreMobileResize: true });

    // Fonts, images and editor reloads shift the layout; keep trigger points true.
    const refresh = () => ScrollTrigger.refresh();
    document.fonts?.ready.then(refresh);
    window.addEventListener('load', refresh);
    document.addEventListener('shopify:section:load', refresh);
    document.addEventListener('shopify:section:unload', () => requestAnimationFrame(refresh));
  }

  /**
   * Splits a heading into masked lines that rise into place. Each mask gets the class
   * split-line-mask, which critical.css extends below the line so descenders aren't cut;
   * lines start low enough to stay hidden behind that taller mask.
   */
  const riseLines = (element, vars) =>
    SplitText.create(element, {
      type: 'lines',
      mask: 'lines',
      linesClass: 'split-line',
      autoSplit: true,
      onSplit: (split) =>
        gsap.from(split.lines, { yPercent: 140, duration: 1.4, ease: 'expo.out', stagger: 0.1, ...vars }),
    });

  /** The pointer's position over an element, from -0.5 to 0.5 on each axis. */
  const pointerOffset = (event, element) => {
    const rect = element.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) / rect.width - 0.5,
      y: (event.clientY - rect.top) / rect.height - 0.5,
    };
  };

  /** Lifts [data-reveal] children into view and raises [data-split] headings line by line. */
  class ScrollReveal extends HTMLElement {
    connectedCallback() {
      if (!canAnimate()) return;

      this.context = gsap.context(() => {
        this.querySelectorAll('[data-split]').forEach((heading) =>
          riseLines(heading, { scrollTrigger: { trigger: heading, start: 'top 88%', once: true } })
        );

        const items = this.querySelectorAll('[data-reveal]');
        if (!items.length) return;
        gsap.set(items, { autoAlpha: 0, y: 36 });
        ScrollTrigger.batch(items, {
          start: 'top 92%',
          once: true,
          onEnter: (batch) =>
            gsap.to(batch, { autoAlpha: 1, y: 0, duration: 1.2, ease: 'power3.out', stagger: 0.12, overwrite: true }),
        });
      }, this);
    }

    disconnectedCallback() {
      this.context?.revert();
    }
  }

  /**
   * Tilts the hero's fan toward the pointer, and spreads it and raises the sun
   * as the page scrolls. The entrance itself is CSS, so the hero shows at once;
   * this only adds to it, from layers the CSS animation doesn't touch.
   */
  class DeckHero extends HTMLElement {
    connectedCallback() {
      if (!canAnimate()) return;

      const fan = this.querySelector('[data-fan]');
      const cards = [...this.querySelectorAll('[data-fan-card]')];
      const sun = this.querySelector('[data-sun-rise]');
      const step = parseFloat(getComputedStyle(fan).getPropertyValue('--step')) || 9;

      this.context = gsap.context(() => {
        // Each card's outer layer adds to the angle its inner layer was dealt to.
        const scroll = { spread: 0, lift: 0 };
        const render = () =>
          cards.forEach((card) =>
            gsap.set(card, { rotation: Number(card.dataset.offset) * step * scroll.spread, yPercent: -scroll.lift })
          );

        const scrub = { trigger: this, start: 'top top', end: 'bottom top', scrub: 0.8 };
        gsap.to(scroll, { spread: 0.3, lift: 14, ease: 'none', onUpdate: render, scrollTrigger: scrub });
        if (sun) gsap.to(sun, { yPercent: -28, ease: 'none', scrollTrigger: scrub });

        if (finePointer.matches) {
          const turnY = gsap.quickTo(fan, 'rotationY', { duration: 1.2, ease: 'power3.out' });
          const turnX = gsap.quickTo(fan, 'rotationX', { duration: 1.2, ease: 'power3.out' });
          this.onPointerMove = (event) => {
            const { x, y } = pointerOffset(event, this);
            turnY(x * 10);
            turnX(-y * 6);
          };
          this.addEventListener('pointermove', this.onPointerMove);
        }
      }, this);
    }

    disconnectedCallback() {
      this.removeEventListener('pointermove', this.onPointerMove);
      this.context?.revert();
    }
  }

  /** From tablet width up, pins the ritual and turns its card as the steps scroll by; on phones, a tap turns it. */
  class DeckRitual extends HTMLElement {
    connectedCallback() {
      this.card = this.querySelector('[data-flip-card]');
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.card.addEventListener('click', this.onCardClick);

      if (!canAnimate()) return;
      this.media = gsap.matchMedia();
      this.media.add('(min-width: 750px)', () => this.pin());
    }

    pin() {
      this.card.classList.remove('is-flipped');
      this.card.setAttribute('aria-pressed', 'false');
      this.classList.add('is-pinned');

      const timeline = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: this.querySelector('[data-pin]'),
          pin: true,
          start: 'top top',
          end: () => `+=${window.innerHeight * 1.5}`,
          scrub: 0.8,
          invalidateOnRefresh: true,
          onUpdate: ({ progress }) => this.setStep(progress),
        },
      });
      timeline
        .fromTo(this.card, { rotation: -5, yPercent: 8 }, { rotation: 0, yPercent: 0, duration: 0.25 }, 0)
        .to(this.querySelector('[data-flip-inner]'), { rotationY: 180, duration: 0.3, ease: 'power2.inOut' }, 0.33)
        .fromTo(
          this.querySelector('[data-highlight]'),
          { autoAlpha: 0, scale: 0.94 },
          { autoAlpha: 1, scale: 1, duration: 0.12, ease: 'power2.out' },
          0.72
        )
        .fromTo(this.querySelector('[data-progress]'), { scaleY: 0 }, { scaleY: 1, duration: 1 }, 0);

      this.trigger = timeline.scrollTrigger;
      this.setStep(0);

      return () => {
        this.trigger = null;
        this.classList.remove('is-pinned');
      };
    }

    setStep(progress) {
      const active = Math.min(this.steps.length - 1, Math.floor(progress * this.steps.length));
      this.steps.forEach((step, i) => step.classList.toggle('is-active', i === active));
    }

    onCardClick = () => {
      if (this.trigger) {
        // While pinned, the card follows the scroll, so a click moves on a step.
        const { start, end, progress } = this.trigger;
        const next = (Math.floor(progress * this.steps.length) + 1) % this.steps.length;
        window.scrollTo({ top: start + (end - start) * ((next + 0.5) / this.steps.length), behavior: 'smooth' });
        return;
      }
      const flipped = this.card.classList.toggle('is-flipped');
      this.card.setAttribute('aria-pressed', String(flipped));
    };

    disconnectedCallback() {
      this.card.removeEventListener('click', this.onCardClick);
      this.media?.revert();
    }
  }

  /** Drifts the band's rows of cards in opposite directions as the page scrolls past. */
  class CardStrip extends HTMLElement {
    connectedCallback() {
      if (!canAnimate()) return;

      this.context = gsap.context(() => {
        this.querySelectorAll('[data-row]').forEach((row) => {
          // How far the row can travel before its far end would come into view.
          const travel = () => Math.max(0, row.scrollWidth - this.clientWidth);
          const leftward = row.dataset.direction === '-1';
          gsap.fromTo(
            row,
            { x: () => (leftward ? 0 : -travel()) },
            {
              x: () => (leftward ? -travel() : 0),
              ease: 'none',
              scrollTrigger: { trigger: this, start: 'top bottom', end: 'bottom top', scrub: 0.6, invalidateOnRefresh: true },
            }
          );
        });
      }, this);
    }

    disconnectedCallback() {
      this.context?.revert();
    }
  }

  /** A stack of sample cards to swipe through, turn over and tilt. */
  class DeckBrowser extends HTMLElement {
    // Where the top four cards sit; the rest wait unseen at the back.
    static slots = [
      { yPercent: 0, scale: 1, rotation: 0 },
      { yPercent: 3, scale: 0.955, rotation: -2.6 },
      { yPercent: 6, scale: 0.91, rotation: 2.2 },
      { yPercent: 9, scale: 0.865, rotation: -1.4 },
    ];

    connectedCallback() {
      if (!hasGsap) return;

      this.stack = this.querySelector('[data-stack]');
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.order = this.cards.map((_, i) => i);
      this.info = this.querySelector('[data-info]');
      this.fields = Object.fromEntries([...this.querySelectorAll('[data-field]')].map((el) => [el.dataset.field, el]));
      this.turnButton = this.querySelector('[data-turn]');

      this.querySelector('[data-next]').addEventListener('click', this.onNext);
      this.querySelector('[data-prev]').addEventListener('click', this.onPrevious);
      this.turnButton.addEventListener('click', this.onTurn);
      this.stack.addEventListener('keydown', this.onKeydown);
      this.stack.addEventListener('pointerdown', this.onPointerDown);
      if (finePointer.matches && canAnimate()) {
        this.stack.addEventListener('pointermove', this.onHover);
        this.stack.addEventListener('pointerleave', this.onHoverEnd);
      }

      this.layout(0);
    }

    get top() {
      return this.cards[this.order[0]];
    }

    /** Scales animation durations to nothing for reduced motion. */
    time(seconds) {
      return reducedMotion.matches ? 0 : seconds;
    }

    layout(duration = 0.8) {
      const { slots } = DeckBrowser;
      this.order.forEach((index, position) => {
        const card = this.cards[index];
        card.style.zIndex = this.cards.length - position;
        card.toggleAttribute('inert', position > 0);
        card.setAttribute('aria-hidden', String(position > 0));
        gsap.to(card, {
          ...slots[Math.min(position, slots.length - 1)],
          x: 0,
          // GSAP reads the CSS fallback's translateY as a pixel offset; clear it, or it doubles the slot's yPercent
          y: 0,
          autoAlpha: position < slots.length ? 1 : 0,
          duration: this.time(duration),
          ease: 'power3.out',
          overwrite: 'auto',
        });
      });
    }

    /** Throws the top card away to one side; it goes to the back of the stack. */
    advance(direction = 1) {
      if (this.busy) return;
      this.busy = true;
      const card = this.top;
      this.settle(card);
      gsap.to(card, {
        x: direction * this.stack.offsetWidth * 1.35,
        rotation: direction * 18,
        autoAlpha: 0,
        duration: this.time(0.55),
        ease: 'power2.in',
        overwrite: 'auto',
        onComplete: () => {
          this.order.push(this.order.shift());
          this.unturn(card);
          this.layout();
          this.update();
          this.busy = false;
        },
      });
    }

    /** Brings the card at the back of the stack in from the left, onto the top. */
    retreat() {
      if (this.busy) return;
      this.busy = true;
      this.settle(this.top);
      this.order.unshift(this.order.pop());
      const card = this.top;
      this.unturn(card);
      gsap.set(card, { x: -this.stack.offsetWidth * 1.35, rotation: -18, autoAlpha: 0 });
      this.layout();
      this.update();
      gsap.delayedCall(this.time(0.45), () => (this.busy = false));
    }

    turn() {
      this.turned = !this.turned;
      gsap.to(this.top.querySelector('[data-card-flip]'), {
        rotationY: this.turned ? 180 : 0,
        duration: this.time(0.9),
        ease: 'power3.inOut',
      });
      this.turnButton.setAttribute('aria-pressed', String(this.turned));
    }

    unturn(card) {
      gsap.set(card.querySelector('[data-card-flip]'), { rotationY: 0 });
      this.turned = false;
      this.turnButton.setAttribute('aria-pressed', 'false');
    }

    settle(card) {
      gsap.to(card.querySelector('[data-card-tilt]'), {
        rotationX: 0,
        rotationY: 0,
        duration: this.time(0.6),
        ease: 'power3.out',
        overwrite: 'auto',
      });
    }

    /** Shows the top card's name, author and keywords beside the stack. */
    update() {
      const { name, author, keywords } = this.top.dataset;
      this.fields.position.textContent = String(this.order[0] + 1).padStart(2, '0');
      this.fields.name.textContent = name;
      this.fields.author.textContent = author;
      this.fields.author.hidden = !author;
      this.fields.keywords.textContent = keywords;
      if (canAnimate()) gsap.fromTo(this.info, { autoAlpha: 0, y: 10 }, { autoAlpha: 1, y: 0, duration: 0.6 });
    }

    onNext = () => this.advance(1);

    onPrevious = () => this.retreat();

    onTurn = () => this.turn();

    onKeydown = (event) => {
      const action = { ArrowRight: this.onNext, ArrowLeft: this.onPrevious, Enter: this.onTurn, ' ': this.onTurn }[
        event.key
      ];
      if (!action) return;
      event.preventDefault();
      action();
    };

    onPointerDown = (event) => {
      if (this.busy || event.button > 0 || !this.top.contains(event.target)) return;
      const card = this.top;
      this.drag = { card, startX: event.clientX, x: 0, time: performance.now(), moved: false };
      card.setPointerCapture(event.pointerId);
      card.addEventListener('pointermove', this.onDrag);
      card.addEventListener('pointerup', this.onDrop);
      card.addEventListener('pointercancel', this.onDrop);
    };

    onDrag = (event) => {
      const { drag } = this;
      drag.x = event.clientX - drag.startX;
      if (!drag.moved && Math.abs(drag.x) > 6) {
        drag.moved = true;
        this.settle(drag.card);
      }
      if (drag.moved) gsap.set(drag.card, { x: drag.x, rotation: drag.x * 0.06 });
    };

    onDrop = (event) => {
      const { card, x, moved, time } = this.drag;
      this.drag = null;
      card.removeEventListener('pointermove', this.onDrag);
      card.removeEventListener('pointerup', this.onDrop);
      card.removeEventListener('pointercancel', this.onDrop);

      // A tap without a drag turns the card over.
      if (!moved) {
        if (event.type === 'pointerup') this.turn();
        return;
      }
      const speed = Math.abs(x) / (performance.now() - time);
      if (Math.abs(x) > this.stack.offsetWidth * 0.28 || speed > 0.65) {
        this.advance(Math.sign(x));
      } else {
        gsap.to(card, { x: 0, rotation: 0, duration: this.time(0.8), ease: 'elastic.out(1, 0.65)' });
      }
    };

    onHover = (event) => {
      if (this.drag || this.busy) return;
      const { x, y } = pointerOffset(event, this.stack);
      const card = this.top;
      gsap.to(card.querySelector('[data-card-tilt]'), {
        rotationY: x * 16,
        rotationX: -y * 16,
        duration: 0.6,
        ease: 'power3.out',
        overwrite: 'auto',
      });
      card.style.setProperty('--glare-x', `${(x + 0.5) * 100}%`);
      card.style.setProperty('--glare-y', `${(y + 0.5) * 100}%`);
    };

    onHoverEnd = () => this.settle(this.top);

    disconnectedCallback() {
      if (hasGsap) gsap.killTweensOf(this.cards ?? []);
    }
  }

  /** Brightens a quote word by word as it scrolls through view, and opens its dark panel to full width. */
  class WordReveal extends HTMLElement {
    connectedCallback() {
      if (!canAnimate()) return;

      this.context = gsap.context(() => {
        const { words } = SplitText.create(this.querySelector('[data-words]'), { type: 'words' });
        gsap.fromTo(
          words,
          { opacity: 0.18 },
          {
            opacity: 1,
            ease: 'none',
            stagger: 0.12,
            scrollTrigger: { trigger: this, start: 'top 70%', end: 'bottom 60%', scrub: true },
          }
        );
        gsap.fromTo(
          this.querySelector('[data-curtain]'),
          { clipPath: 'inset(8% 6% round 2rem)' },
          {
            clipPath: 'inset(0% 0% round 0rem)',
            ease: 'none',
            scrollTrigger: { trigger: this, start: 'top bottom', end: 'top 25%', scrub: true },
          }
        );
      }, this);
    }

    disconnectedCallback() {
      this.context?.revert();
    }
  }

  /** Tilts its [data-tilt-target] toward the pointer, like a card turned in the hand. */
  class TiltCard extends HTMLElement {
    connectedCallback() {
      if (!canAnimate() || !finePointer.matches) return;

      const target = this.querySelector('[data-tilt-target]');
      const max = Number(this.dataset.max) || 10;
      const turnX = gsap.quickTo(target, 'rotationX', { duration: 0.8, ease: 'power3.out' });
      const turnY = gsap.quickTo(target, 'rotationY', { duration: 0.8, ease: 'power3.out' });

      this.onMove = (event) => {
        const { x, y } = pointerOffset(event, this);
        turnY(x * max);
        turnX(-y * max);
      };
      this.onLeave = () => {
        turnX(0);
        turnY(0);
      };
      this.addEventListener('pointermove', this.onMove);
      this.addEventListener('pointerleave', this.onLeave);
    }

    disconnectedCallback() {
      this.removeEventListener('pointermove', this.onMove);
      this.removeEventListener('pointerleave', this.onLeave);
    }
  }

  /** Shows a compact buy bar once the page's own buy buttons ([data-buy-anchor]) have scrolled out of view. */
  class StickyBuy extends HTMLElement {
    connectedCallback() {
      const anchors = [...document.querySelectorAll('[data-buy-anchor]')];
      if (!anchors.length) return;

      const inView = new Set();
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => (isIntersecting ? inView.add(target) : inView.delete(target)));
        // Only once the first button is above the screen, so the bar never shows on arrival.
        const passedFirst = anchors[0].getBoundingClientRect().bottom < 0;
        this.toggle(passedFirst && inView.size === 0);
      });
      anchors.forEach((anchor) => this.observer.observe(anchor));
    }

    toggle(visible) {
      this.classList.toggle('is-visible', visible);
      this.inert = !visible;
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  /** − and + buttons around a quantity field. */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.addEventListener('click', this.onClick);
    }

    onClick = (event) => {
      const button = event.target.closest('[data-step]');
      if (!button) return;
      const min = Number(this.input.min) || 1;
      const max = Number(this.input.max) || 99;
      const value = (Number(this.input.value) || min) + Number(button.dataset.step);
      this.input.value = Math.min(max, Math.max(min, value));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }
  }

  /** Opens and closes a <details> answer with a height animation. */
  class SmoothDetails extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.answer = this.querySelector('[data-answer]');
      this.summary = this.querySelector('summary');
      this.summary.addEventListener('click', this.onToggle);
    }

    onToggle = (event) => {
      if (reducedMotion.matches || !this.answer.animate) return;
      event.preventDefault();

      const opening = !this.details.open;
      if (opening) this.details.open = true;
      const height = `${this.answer.scrollHeight}px`;

      this.animation?.cancel();
      this.animation = this.answer.animate(
        { height: opening ? ['0px', height] : [height, '0px'], opacity: opening ? [0, 1] : [1, 0] },
        { duration: 500, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' }
      );
      this.animation.onfinish = () => {
        if (!opening) this.details.open = false;
        this.animation = null;
        if (hasGsap) ScrollTrigger.refresh();
      };
    };

    disconnectedCallback() {
      this.summary.removeEventListener('click', this.onToggle);
    }
  }

  const elements = {
    'scroll-reveal': ScrollReveal,
    'deck-hero': DeckHero,
    'deck-ritual': DeckRitual,
    'card-strip': CardStrip,
    'deck-browser': DeckBrowser,
    'word-reveal': WordReveal,
    'tilt-card': TiltCard,
    'sticky-buy': StickyBuy,
    'quantity-input': QuantityInput,
    'smooth-details': SmoothDetails,
  };
  Object.entries(elements).forEach(([name, element]) => {
    if (!customElements.get(name)) customElements.define(name, element);
  });
})();
